import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import AppImage from '../AppImage';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/authService';

export default function TopNavbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);

  const navigationItems = [
    {
      label: 'Dashboard',
      icon: 'LayoutDashboard',
      path: '/operations-dashboard'
    },
    {
      label: 'Trading',
      icon: 'Ship',
      dropdown: [
        { label: 'Shipment Register', path: '/shipment-register', icon: 'Ship' },
        { label: 'Cost Register', path: '/cost-register', icon: 'DollarSign' },
        { label: 'Fee Register', path: '/fee-register', icon: 'Receipt' }
      ]
    },
    {
      label: 'Finance',
      icon: 'Wallet',
      dropdown: [
        { label: 'General Ledger', path: '/general-ledger', icon: 'BookOpen' },
        { label: 'Accounts Payable', path: '/accounts-payable', icon: 'CreditCard' },
        { label: 'Accounts Receivable', path: '/accounts-receivable', icon: 'Wallet' },
        { label: 'Invoices', path: '/invoices', icon: 'FileText' },
        { label: 'Bank Reconciliation', path: '/bank-reconciliation', icon: 'Landmark' },
        { label: 'Exchange Rates', path: '/exchange-rates', icon: 'ArrowLeftRight' },
        { label: 'Financial Statements', path: '/financial-statements', icon: 'TrendingUp' }
      ]
    },
    {
      label: 'Operations',
      icon: 'AlertTriangle',
      dropdown: [
        { label: 'Workflow Management', path: '/workflow-management', icon: 'GitBranch' },
        { label: 'Issue & Risk Log', path: '/issue-and-risk-log', icon: 'AlertTriangle' },
        { label: 'Monthly Review Notes', path: '/monthly-review-notes', icon: 'FileText' }
      ]
    },
    {
      label: 'Admin',
      icon: 'Settings',
      dropdown: [
        { label: 'User & Role Management', path: '/user-and-role-management', icon: 'Users' },
        { label: 'Master Data Management', path: '/master-data-management', icon: 'Database' },
        { label: 'Document Management', path: '/document-management-center', icon: 'FolderOpen' }
      ]
    },
    {
      label: 'Reports',
      icon: 'BarChart3',
      path: '/reports-and-analytics'
    }
  ];

  const handleNavigation = (path) => {
    navigate(path);
    setIsMenuOpen(false);
    setActiveDropdown(null);
  };

  const isActive = (path) => location?.pathname === path;

  const handleSignOut = async () => {
    try {
      await authService?.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const toggleDropdown = (label) => {
    setActiveDropdown(activeDropdown === label ? null : label);
  };

  return (
    <nav className="top-navbar">
      <div className="top-navbar-container">
        {/* Logo Section */}
        <div className="top-navbar-logo">
          <AppImage
            src="/assets/images/RVA2Logo-Var_1_-1765525605212.png"
            alt="Right Vectors logo featuring navy blue checkmark design with bronze accent"
            className="h-8 w-auto object-contain"
          />
          <span className="top-navbar-logo-text">HCC</span>
        </div>

        {/* Desktop Navigation */}
        <div className="top-navbar-menu">
          {navigationItems?.map((item, index) => (
            <div key={index} className="top-navbar-menu-item-wrapper">
              {item?.dropdown ? (
                <div className="relative">
                  <button
                    onClick={() => toggleDropdown(item?.label)}
                    className={`top-navbar-menu-item ${
                      item?.dropdown?.some(sub => isActive(sub?.path)) ? 'active' : ''
                    }`}
                  >
                    <Icon name={item?.icon} size={18} />
                    <span>{item?.label}</span>
                    <Icon name="ChevronDown" size={16} />
                  </button>
                  {activeDropdown === item?.label && (
                    <div className="top-navbar-dropdown">
                      {item?.dropdown?.map((subItem, subIndex) => (
                        <button
                          key={subIndex}
                          onClick={() => handleNavigation(subItem?.path)}
                          className={`top-navbar-dropdown-item ${
                            isActive(subItem?.path) ? 'active' : ''
                          }`}
                        >
                          <Icon name={subItem?.icon} size={16} />
                          <span>{subItem?.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => handleNavigation(item?.path)}
                  className={`top-navbar-menu-item ${isActive(item?.path) ? 'active' : ''}`}
                >
                  <Icon name={item?.icon} size={18} />
                  <span>{item?.label}</span>
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Right Section */}
        <div className="top-navbar-actions">
          {/* User Profile */}
          {profile && (
            <div className="flex items-center gap-3">
              <div className="hidden md:flex flex-col items-end">
                <span className="text-sm font-medium text-foreground">
                  {profile?.fullName || 'User'}
                </span>
                <span className="text-xs text-muted-foreground">
                  {profile?.role?.replace(/_/g, ' ') || 'User'}
                </span>
              </div>
              <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center text-white text-sm font-semibold">
                {profile?.fullName?.charAt(0) || profile?.email?.charAt(0) || 'U'}
              </div>
            </div>
          )}

          {/* Sign Out Button */}
          <button
            onClick={handleSignOut}
            className="top-navbar-signout-btn"
            title="Sign Out"
          >
            <Icon name="LogOut" size={18} />
          </button>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="top-navbar-mobile-toggle"
          >
            <Icon name={isMenuOpen ? 'X' : 'Menu'} size={24} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="top-navbar-mobile-menu">
          {navigationItems?.map((item, index) => (
            <div key={index} className="border-b border-border last:border-b-0">
              {item?.dropdown ? (
                <div>
                  <button
                    onClick={() => toggleDropdown(item?.label)}
                    className="top-navbar-mobile-item"
                  >
                    <div className="flex items-center gap-3">
                      <Icon name={item?.icon} size={18} />
                      <span>{item?.label}</span>
                    </div>
                    <Icon name="ChevronDown" size={16} />
                  </button>
                  {activeDropdown === item?.label && (
                    <div className="pl-10 pb-2">
                      {item?.dropdown?.map((subItem, subIndex) => (
                        <button
                          key={subIndex}
                          onClick={() => handleNavigation(subItem?.path)}
                          className={`top-navbar-mobile-subitem ${
                            isActive(subItem?.path) ? 'active' : ''
                          }`}
                        >
                          <Icon name={subItem?.icon} size={16} />
                          <span>{subItem?.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => handleNavigation(item?.path)}
                  className={`top-navbar-mobile-item ${isActive(item?.path) ? 'active' : ''}`}
                >
                  <Icon name={item?.icon} size={18} />
                  <span>{item?.label}</span>
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </nav>
  );
}